import { useState, useMemo, useEffect, useRef } from 'react';
import {
  Button,
  TextField,
  InputAdornment,
  Box,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemButton,
  ListItemText,
  Divider,
  Badge,
  Stack,
  IconButton
} from '@mui/material';
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from '@mui/icons-material/Add';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import { BuildersAndProjectsExpandable } from './BuildersAndProjectsExpandable';
import { WithPermission } from '../../Auth/WithPermission';
import { useNavigate } from 'react-router-dom';
import ContentLayout from '../ContentLayout/ContentLayout';
import { FiltersButton } from './FiltersButton';
import { FileIconBadge } from './FileIconBadge';

export const PlansTableLayout = ({ addPlan, builders, setBuilders, handleArchivePlan,
  addNewPlanToBuildersList, highlightPlanId, setHighlightPlanId, selectedBuilderId, setSelectedBuilderId, fileBadges }) => {

  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    notReady: false,
    today: false,
    yesterday: false,
    last7: false,
  });

  const hasAnyFilters =
    filters.notReady ||
    filters.today ||
    filters.yesterday || 
    filters.last7;

  const [expandedBuildersInBuiAndProExp, setExpandedBuildersInBuiAndProExp] = useState(() => {
    const saved = localStorage.getItem('expandedBuildersInBuiAndProExp');
    return saved ? JSON.parse(saved) : {};
  });

  const [expandedProjects, setExpandedProjects] = useState(() => {
    const saved = localStorage.getItem('expandedProjects');
    return saved ? JSON.parse(saved) : {};
  });

  // Handlers for toggling expansion in BuildersAndProjectsExpandable.js
  const handleBuilderInBuiAndProExpToggle = (builderId, isExpanded) => {
    const newState = {
      ...expandedBuildersInBuiAndProExp,
      [builderId]: isExpanded
    };
    setExpandedBuildersInBuiAndProExp(newState);
    localStorage.setItem('expandedBuildersInBuiAndProExp', JSON.stringify(newState));
  };

  const handleProjectToggle = (projectId, isExpanded) => {
    const newState = {
      ...expandedProjects,
      [projectId]: isExpanded
    };
    setExpandedProjects(newState);
    localStorage.setItem('expandedProjects', JSON.stringify(newState));
  };

  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef(null);

  const [filtersDisabled, setFiltersDisabled] = useState(false);

  const savedFiltersRef = useRef();
  const prevSearchRef = useRef('');

  useEffect(() => {
    const prev = prevSearchRef.current;
    const prevTrim = prev.trim();
    const currTrim = searchTerm.trim();

    // search started
    if (currTrim && !prevTrim) {
      // save current filters
      savedFiltersRef.current = { ...filters };
      // clear filters while searching and disable filters UI
      setFilters({ notReady: false, today: false, yesterday: false, last7: false });
      setFiltersDisabled(true);
    }

    // search ended
    else if (!currTrim && prevTrim) {
      // restore the saved filters
      setFilters(savedFiltersRef.current);
      setFiltersDisabled(false);
    }

    prevSearchRef.current = searchTerm;
    // intentionally depend only on searchTerm to detect transitions.
  }, [searchTerm]);

  const filterBuilders = (data, term) => {
    if (!term.trim()) return data;

    const terms = term.toLowerCase().split(/\s+/).filter(t => t);

    return data
      .map(builder => {
        const filteredPlans = (builder.plans || []).filter(plan =>
          terms.every(term =>
            plan.planNumber?.toLowerCase().includes(term) ||
            plan.project?.toLowerCase().includes(term) ||
            builder.name?.toLowerCase().includes(term) ||
            plan.lot?.toLowerCase().includes(term) ||
            plan.elevation?.toLowerCase().includes(term) ||
            plan.fileName?.toLowerCase().includes(term) ||
            (plan.options || []).some(option =>
              option.name?.toLowerCase().includes(term) ||
              option.planOptionName?.toLowerCase().includes(term)
            ) ||
            (plan.buildStages || []).some(stage =>
              stage.name?.toLowerCase().includes(term) ||
              stage.buildStageName?.toLowerCase().includes(term)
            )
          )
        );

        if (filteredPlans.length > 0) {
          return { ...builder, plans: filteredPlans };
        }
        return null;
      })
      .filter(Boolean);
  };


  const filteredBuilders = useMemo(
    () => filterBuilders(builders || [], searchTerm),
    [builders, searchTerm]
  );

  const showMarkers = !!searchTerm.trim();

  const [isSticky, setIsSticky] = useState(false);
  const sentinelRef = useRef(null);

  const plansMenuRef = useRef(null);

  useEffect(() => {
    if (builders.length > 0)
      setSelectedBuilderId(builders[0].id)

    const setCssVar = () => {
      if (plansMenuRef.current) {
        const h = Math.round(plansMenuRef.current.getBoundingClientRect().height);
        document.documentElement.style.setProperty('--app-plans-menu-height', `${h}px`);
      }
    };

    setCssVar();
    window.addEventListener('resize', setCssVar);
    return () => window.removeEventListener('resize', setCssVar);
  }, []);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || typeof window === 'undefined') return;

    const cssVar = getComputedStyle(document.documentElement).getPropertyValue('--app-header-height');
    const topPx = parseInt(cssVar, 10);

    // When sentinel is NOT intersecting (i.e. scrolled past), menu is sticky
    const observer = new IntersectionObserver(
      ([entry]) => setIsSticky(!entry.isIntersecting),
      {
        root: null, // viewport; if your menu is inside a scrollable container set root to that element
        threshold: 0,
        rootMargin: `-${topPx}px 0px 0px 0px` // shift the trigger down by header height
      }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  function startOfDay(d) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  function daysBetween(a, b) {
    const ms = 1000 * 60 * 60 * 24;
    return Math.floor((startOfDay(a) - startOfDay(b)) / ms);
  }

  function isWithinLastDays(dateStr, days) {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = daysBetween(now, date);
    return diffDays >= 0 && diffDays < days;
  }

  function isWithinLastDaysEndingYesterday(dateStr, days) {
    if (!dateStr) return false;
    const date = new Date(dateStr);
    if (isNaN(date)) return false;

    const todayStart = startOfDay(new Date());
    const yesterdayStart = new Date(todayStart.getTime() - 24 * 60 * 60 * 1000);
    const earliestStart = new Date(yesterdayStart.getTime() - (days - 1) * 24 * 60 * 60 * 1000);

    const dateStart = startOfDay(date);
    return dateStart >= earliestStart && dateStart <= yesterdayStart;
  }

  const planMatchesFilters = (plan, filters) => {
    if (plan.archived || plan.isPlaceholder) return false;

    if (filters.notReady) {
      if (plan.fileStatus !== "processing" && plan.fileStatus !== "error") {
        return false;
      }
    }

    const anyDateSelected = filters.today || filters.yesterday || filters.last7;
    if (!anyDateSelected) return true;

    const modifiedAt = plan.planModifiedDateTime;

    const matchesToday =
      filters.today && isWithinLastDays(modifiedAt, 1);

    const matchesYesterday =
      filters.yesterday &&
      isWithinLastDays(modifiedAt, 2) &&
      !isWithinLastDays(modifiedAt, 1);

    const matchesLast7 =
      filters.last7 && isWithinLastDaysEndingYesterday(modifiedAt, 7);

    return matchesToday || matchesYesterday || matchesLast7;
  };

  const builderCounts = useMemo(() => {
    const map = new Map();

    if (showMarkers) {
      // use the already filteredBuilders (search) counts
      (filteredBuilders || []).forEach(b => {
        map.set(b.id, (b.plans || []).length);
      });
    } else {
      // when NOT searching, count plans that match the current filters
      (builders || []).forEach(b => {
        const cnt = (b.plans || []).filter(plan => planMatchesFilters(plan, filters)).length;
        if (cnt > 0) map.set(b.id, cnt);
      });
    }

    return map;
  }, [filteredBuilders, builders, showMarkers, filters]);

  const hasAnyPlansWhenFiltered = builderCounts.get(selectedBuilderId) || 0;

  return (
    <>

      <Box
        sx={{
          display: 'flex',
          width: '100%',
        }}
      >
        <Drawer
          variant="permanent"
          sx={{
            width: 200,
            flexShrink: 0,
            [`& .MuiDrawer-paper`]: {
              width: 200,
              boxSizing: 'border-box',
              top: 'var(--app-header-height)',
              height: 'calc(100vh - var(--app-header-height))',
              borderRight: '1px solid rgba(0,0,0,0.08)',
            },
            zIndex: 1
          }}
        >
          <Box sx={{ overflow: 'auto', pt: 4 }}>
            <Typography sx={{ paddingX: 2, pb: 1, fontSize: 12, color: 'text.secondary', fontWeight: "bold" }}>
              SELECT BUILDER
            </Typography>

            <List>
              {builders
                .filter(builder => !builder.isArchivedBuilder)
                .map(builder => {
                  const count = builderCounts.get(builder.id) || 0;

                  return (
                    <ListItem key={builder.id} disablePadding>
                      <ListItemButton
                        sx={{
                          paddingY: "2px",
                          '&.Mui-selected, &.Mui-selected:hover': {
                            backgroundColor: '#1976D24D',
                            '& .MuiListItemText-primary': { fontWeight: "bold" }
                          }
                        }}
                        onClick={() => {
                          setSelectedBuilderId(builder.id);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        selected={selectedBuilderId == builder.id}
                      >
                        {/* Left indicator: badge with number (or nothing when 0) */}
                        {count > 0 && searchTerm ? (
                          <ListItemIcon sx={{ minWidth: 28 }}> 
                            <SearchIcon sx={{ fontSize: 20 }} color="warning" /> 
                          </ListItemIcon>
                        ) : hasAnyFilters && count > 0 &&
                        (
                          <ListItemIcon sx={{ minWidth: 30 }}>
                            <Badge
                              badgeContent={count}
                              color="primary"
                              overlap="rectangular"
                              anchorOrigin={{ vertical: 'center' }}
                              sx={{ marginLeft: 0 }}
                            >
                              {/* Badge needs a child element — an invisible box keeps layout tight */}
                              <Box sx={{ width: 20, height: 20 }} />
                            </Badge>
                          </ListItemIcon>
                        )
                        }

                        {/* If you want a placeholder space even when count===0, you can keep an empty ListItemIcon:
                        <ListItemIcon sx={{ minWidth: 40 }} /> */}

                        <ListItemText primary={builder.name} />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
            </List>

            <Divider sx={{ my: 1 }} />

            <List>
              <ListItem disablePadding>
                <ListItemButton
                  sx={{ color: "#1976D2" }}
                  onClick={() => navigate('/archived-plans')}
                >
                  <ListItemIcon sx={{ minWidth: "auto" }}>
                    <Inventory2Icon sx={{ color: "#1976D2", fontSize: 20 }} />
                  </ListItemIcon>

                  <ListItemText
                    sx={{ marginLeft: "5px" }}
                    primary="VIEW ARCHIVE"
                    primaryTypographyProps={{ fontWeight: 500, fontSize: 14 }}
                  />
                </ListItemButton>
              </ListItem>
            </List>
          </Box>
        </Drawer>

        <Box
          component="main"
          sx={{
            m: 0,
            flexGrow: 1,
            px: 3,
          }}
        >
          <ContentLayout title={"Plans"}>

            <div ref={sentinelRef} style={{ height: 1, width: '1px' }} />

            <Box
              id="plans-menu"
              ref={plansMenuRef}
              sx={{
                backgroundColor: "#ECEFF1",//isSticky ? 'white' : 'transparent',
                position: 'sticky',
                top: 'var(--app-header-height, 64px)', // fallback to 64px if var missing
                zIndex: (theme) => theme.zIndex.appBar - 1,
                py: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 3,
                flexWrap: 'wrap'
              }}
            >

              <Stack direction="row" spacing={"25px"} sx={{ marginLeft: "15px", marginRight: 2 }}>
                <FileIconBadge
                  status={"ready"}
                  badgeContent={fileBadges.ready}
                />
                <FileIconBadge
                  status={"processing"}
                  badgeContent={fileBadges.processing}
                />
                <FileIconBadge
                  status={"error"}
                  badgeContent={fileBadges.error}
                />
              </Stack>

              <FiltersButton filters={filters} setFilters={setFilters} filtersDisabled={filtersDisabled} />

              <WithPermission rolesRequired={['admin', 'superadmin', 'useredit']}>
                <Button
                  data-testid="add-plan-button"
                  variant="text"
                  startIcon={<AddIcon />}
                  onClick={addPlan}
                >
                  Add Plan
                </Button>
              </WithPermission>

              <TextField
                placeholder="Find a plan..."
                variant="standard"
                size="small"
                sx={{ ml: 'auto' }}
                value={searchTerm}
                inputRef={searchInputRef}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    // clear on Escape key
                    setSearchTerm('');
                    // keep focus on input
                    searchInputRef.current?.focus();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        aria-label="clear search"
                        onClick={() => {
                          setSearchTerm('');
                          // return focus to input after clearing
                          searchInputRef.current?.focus();
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                  sx: { width: 250 }
                }}
              />
            </Box>

            {searchTerm && filteredBuilders.length === 0
              ?
              <Typography variant="h4" sx={{ textAlign: "center" }}>
                No matching results anywhere
              </Typography>
              :
              <BuildersAndProjectsExpandable builderId={selectedBuilderId} builders={filteredBuilders} setBuilders={setBuilders} searchTerm={searchTerm}
                expandedProjects={expandedProjects} onProjectToggle={handleProjectToggle}
                expandedBuilders={expandedBuildersInBuiAndProExp} onBuilderToggle={handleBuilderInBuiAndProExpToggle}
                handleArchivePlan={handleArchivePlan} addNewPlanToBuildersList={addNewPlanToBuildersList}
                highlightPlanId={highlightPlanId} setHighlightPlanId={setHighlightPlanId}
                filters={filters} setSelectedBuilderId={setSelectedBuilderId} 
                hasAnyPlansWhenFiltered={hasAnyPlansWhenFiltered} hasAnyFilters={hasAnyFilters} planMatchesFilters={planMatchesFilters}/>
            }

          </ContentLayout>
        </Box>
      </Box>

    </>
  );
};